import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuthedApi, errMsg } from "@/lib/api";
import { BRANCHES, STATUS } from "@/lib/format";

const EMPTY = { title: "", branch: "", client: "", location: "", address: "", map_url: "", event_date: "", event_time: "", setup_date: "", setup_time: "", teardown_date: "", teardown_time: "", equipment: [], responsible_name: "", responsible_phone: "", status: "agendado", notes: "" };
const inputCls = "h-10 w-full rounded-sm border border-slate-700 bg-slate-950 px-3 text-sm outline-none transition-colors focus:border-amber-500";

const Field = ({ label, children }) => (
  <label className="block space-y-1"><span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{label}</span>{children}</label>
);

export const EventFormDialog = ({ open, onOpenChange, event, onSaved }) => {
  const api = useAuthedApi();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF(event ? { ...EMPTY, ...event } : EMPTY);
  }, [open, event]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const inp = (k, props = {}) => <input data-testid={`event-form-${k.replace(/_/g, "-")}`} className={inputCls} value={f[k]} onChange={set(k)} {...props} />;
  const setEq = (i, k, v) => setF((p) => ({ ...p, equipment: p.equipment.map((e, j) => (j === i ? { ...e, [k]: v } : e)) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...f, equipment: f.equipment.filter((x) => x.name.trim()).map((x) => ({ name: x.name.trim(), quantity: Number(x.quantity) || 1 })) };
      const saved = await api(event ? "put" : "post", event ? `/events/${event.id}` : "/events", body);
      toast.success(event ? "Evento atualizado" : "Evento criado");
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="event-form-dialog" className="max-h-[92vh] max-w-2xl overflow-y-auto border-slate-800 bg-slate-900">
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{event ? "Editar evento" : "Novo evento"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nome do evento *">{inp("title", { required: true })}</Field>
            <Field label="Filial *">
              <select data-testid="event-form-branch" required className={inputCls} value={f.branch} onChange={set("branch")}>
                <option value="" disabled>Selecione a filial</option>
                {Object.entries(BRANCHES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </Field>
            <Field label="Cliente">{inp("client")}</Field>
            <Field label="Local *">{inp("location", { required: true })}</Field>
            <Field label="Endereço">{inp("address")}</Field>
            <Field label="Link do mapa (opcional)">{inp("map_url", { placeholder: "https://maps.google.com/..." })}</Field>
            <Field label="Status">
              <select data-testid="event-form-status" className={inputCls} value={f.status} onChange={set("status")}>
                {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid gap-4 rounded-md border border-slate-800 p-4 sm:grid-cols-3">
            {[["setup", "Montagem"], ["event", "Evento"], ["teardown", "Desmontagem"]].map(([k, l]) => (
              <div key={k} className="space-y-2">
                <Field label={`${l} — data *`}>{inp(`${k}_date`, { type: "date", required: true })}</Field>
                <Field label={`${l} — horário`}>{inp(`${k}_time`, { type: "time" })}</Field>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Equipamentos locados</span>
              <button type="button" data-testid="add-equipment-button" onClick={() => setF((p) => ({ ...p, equipment: [...p.equipment, { name: "", quantity: 1 }] }))} className="inline-flex items-center gap-1 text-sm text-amber-400 hover:text-amber-300"><Plus size={14} />Adicionar</button>
            </div>
            {f.equipment.map((e, i) => (
              <div key={i} className="flex gap-2">
                <input data-testid={`equipment-name-input-${i}`} className={inputCls} placeholder="Equipamento" value={e.name} onChange={(ev) => setEq(i, "name", ev.target.value)} />
                <input data-testid={`equipment-quantity-input-${i}`} type="number" min="1" className={`${inputCls} !w-24 shrink-0`} value={e.quantity} onChange={(ev) => setEq(i, "quantity", ev.target.value)} />
                <button type="button" data-testid={`remove-equipment-button-${i}`} onClick={() => setF((p) => ({ ...p, equipment: p.equipment.filter((_, j) => j !== i) }))} className="px-2 text-slate-500 hover:text-red-400"><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Responsável">{inp("responsible_name")}</Field>
            <Field label="Telefone do responsável">{inp("responsible_phone", { placeholder: "(11) 99999-9999" })}</Field>
          </div>
          <Field label="Observações">
            <textarea data-testid="event-form-notes" rows={3} className={`${inputCls} h-auto py-2`} value={f.notes} onChange={set("notes")} />
          </Field>
          <button disabled={saving} data-testid="save-event-button" className="w-full rounded-sm bg-amber-500 py-3 font-semibold text-white transition-colors hover:bg-amber-400 disabled:opacity-60">{saving ? "Salvando..." : "Salvar evento"}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
